import React, {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {compose} from "redux"
import classNames from "classnames"

// Models
import {Record} from "../../models"

// HOCs
import withRecordData, {WithRecordDataProps} from "../../hoc/withRecordData"

// Components
import {Icon, Button, Snackbar} from "@material-ui/core"
import DataParameter from "../DataParameter"

// Styles
import useStyles from "./styles"

// Hooks
import usePrevious from "../../../../hooks/usePrevious"
import withConclusion, {WithConclusionProps} from "../../hoc/withConclusion"

function CommonParametersSection(props: WithRecordDataProps & WithConclusionProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {commonParameters, parametersUpdating, updateParameters, parametersUpdated, isParameterEditing, toggleParameterEditing, recordConflict} = props;
    const prevParametersUpdating = usePrevious(parametersUpdating)
    const prevParametersUpdated = usePrevious(parametersUpdated)
    const [params, setParams] = useState<Record["commonParameters"] | null>(commonParameters)
    const [openSuccess, setOpenSuccess] = useState(false)
    const [openError, setOpenError] = useState(false)

    useEffect(() => {
        if (typeof prevParametersUpdating !== "undefined" && prevParametersUpdating !== parametersUpdating && !parametersUpdating) {
            setParams(commonParameters)
        }
    }, [parametersUpdating, commonParameters])

    useEffect(() => {
        if (typeof prevParametersUpdated !== "undefined" && prevParametersUpdated !== parametersUpdated) {
            if (parametersUpdated) {
                setOpenSuccess(true)
            } else if (parametersUpdated === false) {
                setOpenError(true)
            }
        }
    }, [parametersUpdated, prevParametersUpdated])

    const handleParamEdit = (param: string, value: string) => {
        setParams({
            ...params,
            [param]: +value,
        })
    }

    if (!params) {
      return (
        <div style={{minWidth: "200px"}}>
          <span>{t("no_params")}</span>
        </div>
      )
    }

    function handleCancelButtonClick() {
        toggleParameterEditing()
    }

    function handleEditButtonClick() {
        toggleParameterEditing()
    }

    function handleSave() {
        toggleParameterEditing()
        updateParameters(params as Record["commonParameters"])
    }

    function renderEditInterpretationButtons() {
        if (isParameterEditing) {
            return (
                <React.Fragment>
                    <Button
                        variant="text"
                        color="secondary"
                        size="small"
                        onClick={handleCancelButtonClick}
                    >
                        {t("records.cancel")}
                    </Button>
                    <Button
                        variant="text"
                        color="primary"
                        size="small"
                        onClick={handleSave}
                    >
                        {t("records.save")}
                    </Button>
                </React.Fragment>
            )
        }

        return (
            <Button
                startIcon={<Icon>edit</Icon>}
                variant="text"
                size="small"
                color="primary"
                onClick={handleEditButtonClick}
            >
                {t("records.edit")}
            </Button>
        )
    }

    return (
        <div>
            <DataParameter
                editable
                param="heartRate"
                onEdit={handleParamEdit}
                unit="bpm"
                isParameterEditing={isParameterEditing}
                upperCasedName
                value={params.heartRate}
                name={t("records.hr")}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="pDuration"
                onEdit={handleParamEdit}
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.pDuration}
                name={`${t("records.interval")} P`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="tDuration"
                onEdit={handleParamEdit}
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.tDuration}
                name={`${t("records.interval")} T`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="prInterval"
                onEdit={handleParamEdit}
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.prInterval}
                name={`${t("records.interval")} PR`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="qrsDuration"
                onEdit={handleParamEdit}
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.qrsDuration}
                name={`${t("records.interval")} QRS`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="qtInterval"
                onEdit={handleParamEdit}
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.qtInterval}
                name={`${t("records.interval")} QT`}
                className={styles.parameter}
            />
            <DataParameter
                unit="ms"
                isParameterEditing={isParameterEditing}
                value={params.qtcInterval}
                name={`${t("records.interval")} QTc`}
                className={classNames(styles.parameter, styles.parameter__space)}
            />
            <DataParameter
                editable
                param="pAxis"
                isParameterEditing={isParameterEditing}
                onEdit={handleParamEdit}
                unit="°"
                value={params.pAxis}
                unitSpace={false}
                name={`${t("records.axis")} P`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="tAxis"
                isParameterEditing={isParameterEditing}
                onEdit={handleParamEdit}
                unit="°"
                value={params.tAxis}
                unitSpace={false}
                name={`${t("records.axis")} T`}
                className={styles.parameter}
            />
            <DataParameter
                editable
                param="qrsAxis"
                isParameterEditing={isParameterEditing}
                onEdit={handleParamEdit}
                unit="°"
                value={params.qrsAxis}
                unitSpace={false}
                name={`${t("records.axis")} QRS`}
                className={styles.parameter}
            />
            {renderEditInterpretationButtons()}
            <Snackbar
                open={openSuccess}
                autoHideDuration={1500}
                message={t("records.parameters_update_success")}
                onClose={() => setOpenSuccess(false)}
            />
            <Snackbar
                open={openError}
                autoHideDuration={1500}
                message={t("records.parameters_update_error")}
                onClose={() => setOpenError(false)}
            />
        </div>
    )
}

export default compose(
    withRecordData,
    withConclusion,
)(CommonParametersSection)
